import { debug, error as logError } from '@/utils/logger';
import {
  readSocialImportErrorBody,
  socialImportRequestErrorFromResponse,
  userFacingSocialImportErrorMessage,
} from '@/utils/socialImportApiErrors';

export const ExtractionMethod = {
  TAGGED_USER: 'tagged_user',
  CAPTION: 'caption',
  HASHTAG: 'hashtag',
  LOCATION_TAG: 'location_tag',
  UNKNOWN: 'unknown',
} as const;

export type ExtractionMethodType = typeof ExtractionMethod[keyof typeof ExtractionMethod];

export interface TaggedUser {
  username: string;
  fullName: string;
  id: string;
  profilePicUrl: string;
}

export interface SocialScraperResponse {
  postId: string;
  shortCode: string;
  fromCache: boolean;
  post: {
    caption: string;
    hashtags: string[];
    locationName: string | null;
    taggedUsers: TaggedUser[];
    url: string;
    type: 'Image' | 'Video' | 'Sidecar';
    displayUrl: string | null;
    images: string[];
    videoUrl: string | null;
    videoViewCount: number | null;
    dimensions: { width: number; height: number };
    ownerUsername: string;
    ownerFullName: string;
    likesCount: number;
    commentsCount: number;
    timestamp: string;
  };
  locations: Array<{
    name: string;
    city: string;
    country: string;
    locationType: 'place' | 'city' | 'country';
    extractionSource: {
      method: ExtractionMethodType;
      detail: string;
      taggedUser: TaggedUser | null;
    };
    place: {
      placeId: string;
      name: string;
      address: {
        full: string;
        short: string;
        city: string;
        country: string;
        countryCode: string;
      };
      coordinates: { lat: number; lng: number };
      category: {
        primary: string;
        primaryDisplay: string;
        all: string[];
      };
      contact: {
        phone: string | null;
        website: string | null;
        googleMapsUrl: string;
      };
      rating: { score: number; totalReviews: number } | null;
      status: string | null;
      photos: Array<{
        name?: string; // New format identifier
        photo_reference?: string; // Old format identifier (fallback)
        author: {
          displayName: string;
          uri: string | null;
        } | null;
      }>;
    } | null;
  }>;
}

export const socialScraperService = {
  /**
   * Scrape Instagram or TikTok post and extract locations
   */
  importFromSocial: async (
    url: string,
    force: boolean = false
  ): Promise<SocialScraperResponse> => {
    debug('[socialScraperService] importFromSocial called', {
      url,
      force,
      timestamp: new Date().toISOString(),
    });
    
    try {
      const baseURL = process.env.NEXT_PUBLIC_APP_API_BASE_URL || 'https://app-be.globethrivers.com';
      const endpoint = `${baseURL}/api/v1/public/social/import?photoFormat=identifiers`;
      
      debug('[socialScraperService] Making API request', {
        endpoint,
        method: 'POST',
        body: { url, force },
      });
      
      const startTime = Date.now();
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url, force }),
      });

      const duration = Date.now() - startTime;
      
      debug('[socialScraperService] API response received', {
        status: response.status,
        statusText: response.statusText,
        duration: `${duration}ms`,
        cached: response.headers.get('x-cache') || 'unknown',
      });

      if (!response.ok) {
        const errorBody = await readSocialImportErrorBody(response);
        const userMessage = userFacingSocialImportErrorMessage(
          response.status,
          response.statusText,
          errorBody,
        );
        logError('[socialScraperService] API error', {
          status: response.status,
          statusText: response.statusText,
          code: errorBody?.code ?? null,
          message: userMessage,
          response: errorBody,
        });
        throw socialImportRequestErrorFromResponse(response, errorBody, userMessage);
      }

      const data = await response.json();
      
      debug('[socialScraperService] API success', {
        postId: data.postId,
        shortCode: data.shortCode,
        fromCache: data.fromCache,
        locationsCount: data.locations?.length || 0,
        locationsWithPlaces: data.locations?.filter((loc: { place: unknown }) => loc.place).length || 0,
        postType: data.post?.type,
        ownerUsername: data.post?.ownerUsername,
      });
      debug('[socialScraperService] Full response data', data);

      return data;
    } catch (error: unknown) {
      logError('[socialScraperService] Error caught', {
        error,
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      logError('Social scraper error:', error);
      throw error;
    }
  },
};

